import { Button, Dialog, Grid, Group, Modal, Stack, Text, Textarea, TextInput, Title } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useEffect, useState } from 'react';
import { BaseInputField } from './BaseInput';

interface ContextInputProps {
    onChange: (context: ContextInputField) => void;
    onCancel: () => void;
    opened: boolean;
    data?: ContextInputField;
    baseData?: BaseInputField
}

export const initialContextValues: ContextInputField = {
    clientName: '',                // 業務稱呼
    mobile: '',             // 聯絡手機
    email: '',              // 電子郵件
    propertyName: '',       // 物件名稱
    address: '',            // 地址
    area: '',               // 權狀坪數
    unitPrice: '',              // 單價
    totalPrice: '',             // 總價
    age: '',                // 屋齡
    completionDate: '', // 完工日期
    usageType: '',         // 使用類型
    parkingType: '',       // 車位形式
    parkingSpaces: '',     // 車位數量
    orientation: '',        // 物件朝向
    forSaleFloor: '',             // 物件樓層
    layout: '',            // 格局
    registeredUsage: '',   // 登記用途
    features: ''           // 特色說明
};

const demoContextValues: ContextInputField = {
    clientName: '陳先生',                // 業務稱呼
    mobile: '0912345678',             // 聯絡手機
    email: 'chen@example.com',        // 電子郵件
    propertyName: '晴山悅景',       // 物件名稱
    address: '新北市新店區安康路三段',            // 地址
    area: '25–48坪',               // 權狀坪數
    unitPrice: '每坪50–60萬',              // 單價
    totalPrice: '1250-2880萬',              // 總價
    age: '此為預售屋',                // 屋齡
    completionDate: "2026年12月", // 完工日期
    usageType: '預售屋 / 集合住宅',         // 使用類型
    parkingType: '附地下停車場',       // 車位形式
    parkingSpaces: '1位',     // 車位數量
    orientation: '坐南朝北',        // 物件朝向
    forSaleFloor: '地上15層、地下3層，共128戶',             // 物件樓層
    layout: '2房2廳1衛（25–30坪）\n3房2廳2衛（35–42坪）\n4房2廳2衛（45–48坪）',            // 格局
    registeredUsage: '住宅',   // 登記用途
    features: `公共設施：健身房、交誼廳、兒童遊戲區、空中花園
交通機能：
步行8分鐘可達安康輕軌站，銜接捷運小碧潭站
鄰近北宜高安坑交流道，約15分鐘可達台北市中心
學區資源：
安康國小、安康國中學區內，步行約10分鐘
生活機能：
全聯福利中心與家樂福便利型分店車程約5分鐘
安坑運動中心、社區公園步行可達
醫療設施：
鄰近耕莘醫院安康分院（車程約6分鐘）
`           // 特色說明
};

export function ContextInputPage(props: ContextInputProps) {
    const { onChange, onCancel, opened, data, baseData } = props;
    const form = useForm<ContextInputField>({
        initialValues: initialContextValues
    });

    const [cancelable, setCancelable] = useState(false);

    useEffect(() => {
        if (data) {
            form.setValues(data);
            setCancelable(true);
        }
        else {
            form.setValues({
                ...initialContextValues,
                ...(baseData ?? {}),
            });
            setCancelable(false);
        }
    }, [data]);

    const handleSubmit = (values: ContextInputField) => {
        onChange(values);
    };

    const handleCancel = () => {
        if (data) form.setValues(data);
        onCancel();
    }

    function resetData() {
        form.setValues({
            propertyName: demoContextValues.propertyName,
            address: demoContextValues.address,
            area: demoContextValues.area,
            unitPrice: demoContextValues.unitPrice,
            totalPrice: demoContextValues.totalPrice,
            age: demoContextValues.age,
            completionDate: demoContextValues.completionDate,
            usageType: demoContextValues.usageType,
            parkingType: demoContextValues.parkingType,
            parkingSpaces: demoContextValues.parkingSpaces,
            orientation: demoContextValues.orientation,
            forSaleFloor: demoContextValues.forSaleFloor,
            layout: demoContextValues.layout,
            registeredUsage: demoContextValues.registeredUsage,
            features: demoContextValues.features
        });
    }

    return <>
        <Modal
            opened={opened}
            onClose={() => { }}
            withCloseButton={false}
            zIndex={300}
            size={"xl"} // xs, sm, md, lg, xl
            styles={{
                header: {
                    background: "#241E1E"
                },
                content: {
                    borderRadius: '15px',
                    background: " linear-gradient(180deg, #241E1E 0%, #09080E 100%)",
                    border: '2px solid #C09C54',
                    overflow: 'hidden',
                },
                body: {
                    padding: '2rem',
                    maxHeight: '90vh',
                    overflowY: 'auto', // 啟用垂直滾動
                    overflowX: 'hidden', // 隱藏水平滾動
                },
            }}
            withOverlay={false}
        >
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="sm">
                    <Title order={2} ta="center" c="#BF9376">
                        AI地產業務客服員 - 試用體驗
                    </Title>
                    <Grid>
                        <Grid.Col span={10}>
                            <Stack gap={"xs"}>
                                <Text c="#BF9376">
                                    測試用房產資料：   <Text component='span' c="#FAFAFA">試用途中可修改，資料暫存於瀏覽器，離開頁面後將不保留。</Text>
                                </Text>
                            </Stack>
                        </Grid.Col>
                        <Grid.Col span={2}>
                            <Button
                                color='#A77145'
                                onClick={() => resetData()}
                            >
                                使用預設範本
                            </Button>
                        </Grid.Col>
                    </Grid>
                    <Grid>
                        <Grid.Col span={6}>
                            <TextInput
                                label="業務稱呼"
                                placeholder="請輸入業務稱呼"
                                {...form.getInputProps('name')}
                            />
                        </Grid.Col>
                        <Grid.Col span={6}>
                            <TextInput
                                label="聯絡手機"
                                placeholder="請輸入聯絡手機"
                                {...form.getInputProps('mobile')}
                            />
                        </Grid.Col>
                    </Grid>
                    <Grid>
                        <Grid.Col span={12}>
                            <TextInput
                                label="電子郵件"
                                placeholder="請輸入電子郵件"
                                {...form.getInputProps('email')}
                            />
                        </Grid.Col>
                    </Grid>
                    <Grid>
                        <Grid.Col span={12}>
                            <TextInput
                                label="物件名稱"
                                placeholder="請輸入物件名稱"
                                {...form.getInputProps('propertyName')}
                            />
                        </Grid.Col>
                    </Grid>
                    <Grid>
                        <Grid.Col span={12}>
                            <TextInput
                                label="地址"
                                placeholder="請輸入地址"
                                {...form.getInputProps('address')}
                            />
                        </Grid.Col>
                    </Grid>
                    <Grid>
                        <Grid.Col span={6}>
                            <TextInput
                                label="權狀坪數"
                                placeholder="請輸入權狀坪數"
                                {...form.getInputProps('area')}
                            />
                        </Grid.Col>

                        <Grid.Col span={6}>
                            <TextInput
                                label="登記用途"
                                placeholder="請輸入登記用途"
                                {...form.getInputProps('registeredUsage')}
                            />
                        </Grid.Col>
                    </Grid>
                    <Grid>
                        <Grid.Col span={6}>
                            <TextInput
                                label="單價"
                                placeholder="請輸入單價"
                                {...form.getInputProps('unitPrice')}
                            />
                        </Grid.Col>
                        <Grid.Col span={6}>
                            <TextInput
                                label="總價"
                                placeholder="請輸入總價"
                                {...form.getInputProps('totalPrice')}
                            />
                        </Grid.Col>
                    </Grid>
                    <Grid>
                        <Grid.Col span={6}>
                            <TextInput
                                label="屋齡"
                                placeholder="請輸入屋齡"
                                {...form.getInputProps('age')}
                            />
                        </Grid.Col>
                        <Grid.Col span={6}>
                            <TextInput
                                label="完工日期"
                                placeholder="請輸入完工日期"
                                {...form.getInputProps('completionDate')}
                            />
                        </Grid.Col>
                    </Grid>
                    <Grid>

                        <Grid.Col span={6}>
                            <TextInput
                                label="使用類型"
                                placeholder="請輸入使用類型"
                                {...form.getInputProps('usageType')}
                            />
                        </Grid.Col>
                        <Grid.Col span={6}>
                            <TextInput
                                label="格局"
                                placeholder="請輸入格局"
                                {...form.getInputProps('layout')}
                            />
                        </Grid.Col>
                    </Grid>
                    <Grid>
                        <Grid.Col span={6}>
                            <TextInput
                                label="車位形式"
                                placeholder="請輸入車位形式"
                                {...form.getInputProps('parkingType')}
                            />
                        </Grid.Col>
                        <Grid.Col span={6}>
                            <TextInput
                                label="車位數量"
                                placeholder="請輸入車位數量"
                                {...form.getInputProps('parkingSpaces')}
                            />
                        </Grid.Col>
                    </Grid>
                    <Grid>

                        <Grid.Col span={6}>
                            <TextInput
                                label="物件朝向"
                                placeholder="請輸入物件朝向"
                                {...form.getInputProps('orientation')}
                            />
                        </Grid.Col>
                        <Grid.Col span={6}>
                            <TextInput
                                label="物件樓層"
                                placeholder="請輸入物件樓層"
                                {...form.getInputProps('forSaleFloor')}
                            />
                        </Grid.Col>

                    </Grid>
                    <Textarea
                        label="特色說明"
                        placeholder="請描述產品特色"
                        autosize
                        rows={5}
                        {...form.getInputProps('features')}
                    />
                    <Group justify="center" mt="md">
                        <Button type="submit" color='#A77145'>{cancelable ? '編輯完成' : '開始體驗'}</Button>
                        {cancelable && <Button color='#5e5e5e' onClick={handleCancel}>取消編輯</Button>}
                    </Group>
                </Stack>
            </form>
        </Modal >
    </>
}

export interface ContextInputField {
    clientName: string;                // 業務稱呼
    mobile: string;             // 聯絡手機
    email: string;              // 電子郵件
    propertyName: string;      // 物件名稱
    address: string;           // 地址
    area: string;             // 權狀坪數
    unitPrice: string;            // 單價
    totalPrice: string;          // 總價
    age: string;              // 屋齡
    completionDate?: string;   // 完工日期
    usageType: string;        // 使用類型
    parkingType: string;      // 車位形式
    parkingSpaces: string;    // 車位數量
    orientation: string;      // 物件朝向
    forSaleFloor: string;           // 可售物件樓層
    layout: string;          // 格局
    registeredUsage: string; // 登記用途
    features: string;        // 特色說明
}